import { Component } from './cart-discount-component.js';
import { morphSection } from './cart-discount-morph.js';
import { CartDiscountAccordion } from './cart-discount-accordion.js';

class DiscountUpdateEvent extends Event {
  /* Creates a new DiscountUpdateEvent */
  constructor(resource, sourceId) {
    super('discount:update', { bubbles: true });
    this.detail = {
      resource,
      sourceId,
    };
  }
}
/* A custom element that applies a discount to the cart. */
class CartDiscount extends Component {
  #getShortSectionId(selector) {
    const el = document.querySelector(selector);
    return el?.getAttribute('id')?.replace('shopify-section-', '') || null;
  }
  get drawerItemsShortId() {
    return this.#getShortSectionId('#mini-cart .shopify-section.shopify-section-group-cart-body-group.Cart-Items');
  }

  get drawerCheckoutShortId() {
    return this.#getShortSectionId('#mini-cart .shopify-section.shopify-section-group-cart-footer-group.Cart-Checkout');
  }

  get drawerFreebiesId() {
    return this.#getShortSectionId('#mini-cart .shopify-section.shopify-section-group-cart-body-group.Cart-Freebies');
  }

  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /* Handles updates to the cart note. */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId, this.drawerItemsShortId, this.drawerCheckoutShortId, this.drawerFreebiesId],
        }),
      });

      const response = await fetch(`${window.themeVariables.routes.cartUpdateUrl}.js`, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];

      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);

      // Update the drawer product items and checkout section in drawer cart
      const sectionUpdates = [
        [this.drawerItemsShortId, data.sections[this.drawerItemsShortId]],
        [this.drawerCheckoutShortId, data.sections[this.drawerCheckoutShortId]],
        [this.drawerFreebiesId, data.sections[this.drawerFreebiesId]],
      ];

      sectionUpdates.forEach(([id, html]) => {
        if (html) morphSection(id, html);
      });
      const customEvent = new CustomEvent('discount:update');
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /* Handles removing a discount from the cart. */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId, this.drawerItemsShortId, this.drawerCheckoutShortId, this.drawerFreebiesId] }),
      });

      const response = await fetch(`${window.themeVariables.routes.cartUpdateUrl}.js`, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);

      // Update the drawer product items and checkout section in drawer cart
      const sectionUpdates = [
        [this.drawerItemsShortId, data.sections[this.drawerItemsShortId]],
        [this.drawerCheckoutShortId, data.sections[this.drawerCheckoutShortId]],
        [this.drawerFreebiesId, data.sections[this.drawerFreebiesId]],
      ];

      sectionUpdates.forEach(([id, html]) => {
        if (html) morphSection(id, html);
      });
      const customEvent = new CustomEvent('discount:update');
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /* Returns an array of existing discount codes. */
  #existingDiscounts() {
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
if (!customElements.get('cart-discount-accordion')) {
  customElements.define('cart-discount-accordion', CartDiscountAccordion);
}

function fetchConfig(type = 'json', config = {}) {
  const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers: (headers),
    body: config.body,
  };
}